import { useMemo } from 'react';
import { useMutation, MutationHookOptions } from '@apollo/client';
import { archiveDestinationMutation } from 'api/mutations/dataSync';

export type ArchiveDestinationVariables = {
  destinationId: string;
};

export type ArchiveDestinationSuccess = {
  destinationId: string;
  archived: boolean;
};

export type ArchiveDestinationFailure = {
  message: string;
};

export type ArchiveDestinationResponse = {
  archiveDestination: ArchiveDestinationSuccess | ArchiveDestinationFailure;
};

export type UseArchiveDestinationOptions = MutationHookOptions<
  ArchiveDestinationResponse,
  ArchiveDestinationVariables
>;

export const useArchiveDestination = (options?: UseArchiveDestinationOptions) => {
  const mutationOptions: UseArchiveDestinationOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      ...options,
    }),
    [options],
  );

  const [archiveDestination, { data, loading: isLoading, error, reset }] = useMutation<
    ArchiveDestinationResponse,
    ArchiveDestinationVariables
  >(archiveDestinationMutation, mutationOptions);

  const isSuccess = data?.archiveDestination && 'destinationId' in data.archiveDestination;
  const isFailure = data?.archiveDestination && 'message' in data.archiveDestination;
  const hasGraphQLError = !!error;
  const errorMessage = isFailure
    ? (data.archiveDestination as ArchiveDestinationFailure).message
    : error?.message || null;

  return {
    archiveDestination,
    isLoading,
    error,
    errorMessage,
    isSuccess,
    isFailure: isFailure || hasGraphQLError,
    reset,
    data: isSuccess ? (data.archiveDestination as ArchiveDestinationSuccess) : null,
  };
};
